/*
  How a set's or a card's grades spread across the scale: one stacked bar, best grade on
  the left, with a legend of counts and shares under it. The set and card headers both
  show it, fed the same seven bands (lib/population), so the two read alike.

  The bar is decoration for the legend, which carries every number as text, so the bar is
  hidden from screen readers. A band too small to see still gets a sliver, so a single
  Flawless in a set of thousands is not invisible.

  The colours are option C of D16 (docs/specs/pop-language-split.md, Robert, 2026-10-20).
  The 10s are one gold family with platinum on top: Flawless #EEF1F6, Pristine the brand
  gold, Gem Mint 10 a deep gold #A9822F. The 9s leave gold for slate, in keeping with the
  event skin's navy chrome: 9.5 #7C86A8 and 9 #525A78, so a 9 no longer reads as a faded
  10. 8.5-and-below and Authentic stay greys, so the eye still goes to the 10s. The new
  ones are literals rather than tokens because D16 fixes them the same in light and dark;
  Pristine stays on the brand-gold token, which is what "brand gold" means in each skin.
  The classes are written out whole for Tailwind's scanner.

  Platinum is nearly the paper colour, so on the light theme a bare Flawless swatch, and
  its segment on the bar's light track, would vanish. It carries an inset hairline in
  both places (EDGE); in dark it is just a faint rim on a bright chip.

  The legend is centred under the bar (D16), which matters most when it wraps: a ragged
  left-aligned second line read as a list that had run out.
*/

import { CONTROL_LABEL } from 'components/styles';
import { formatCount, formatShare, gradeMix, tallyTens, tallyTotal } from 'lib';
import type { GradeBandKey, GradeTally } from 'lib';

const EDGE = 'ring-1 ring-inset ring-pcg-textMuted/40';

const SWATCH: Record<GradeBandKey, string> = {
  flawless: `bg-[#EEF1F6] ${EDGE}`,
  pristine: 'bg-pcg-gold',
  gemMint10: 'bg-[#A9822F]',
  nineFive: 'bg-[#7C86A8]',
  nine: 'bg-[#525A78]',
  lower: 'bg-pcg-textMuted/45',
  authentic: 'bg-pcg-textMuted/20',
};

interface GradeMixBarProps {
  tally: GradeTally;
  /* The legend's heading, e.g. "Grade mix". */
  title?: string;
}

const GradeMixBar = ({ tally, title = 'Grade mix' }: GradeMixBarProps) => {
  const segments = gradeMix(tally);
  if (segments.length === 0) {
    return null;
  }
  const total = tallyTotal(tally);
  const tens = tallyTens(tally);

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <span className={CONTROL_LABEL}>{title}</span>
        {tens > 0 ? (
          <span className="text-sm text-pcg-textMuted">
            <strong className="font-bold text-pcg-goldText">{formatShare((tens / total) * 100)}</strong> graded 10
          </span>
        ) : null}
      </div>
      <div aria-hidden="true" className="flex h-3 w-full gap-0.5 overflow-hidden rounded-full bg-pcg-raised">
        {segments.map(segment => (
          <span
            key={segment.key}
            className={`${SWATCH[segment.key]} h-full first:rounded-l-full last:rounded-r-full`}
            style={{ flexGrow: segment.share, flexBasis: 0, minWidth: '4px' }}
          />
        ))}
      </div>
      <ul className="mt-3 flex flex-wrap justify-center gap-x-5 gap-y-1.5 text-xs">
        {segments.map(segment => (
          <li key={segment.key} className="flex items-center gap-1.5">
            <span aria-hidden="true" className={`${SWATCH[segment.key]} h-2.5 w-2.5 rounded-sm`} />
            <span className="font-semibold text-pcg-text">{segment.label}</span>
            <span className="tabular-nums text-pcg-textMuted">
              {formatCount(segment.count)} · {formatShare(segment.share)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default GradeMixBar;
